export default {
  metadata: {
    version: '1.0.0',
    id: 'service-card-1.0.0',
    name: 'JDSServiceCard',
    description:
      'Service cards are blocks of images and text aiming to promote and connect services.',
    usage:
      'Service cards are used to drive users from one Jio service to another.'
  },
  hierarchy: {
    'root-container': [
      {
        'inner-stack-container': [
          'blur-image',
          {
            'internal-vertical-container': [
              {
                'image-container': ['jds_image']
              },
              {
                'content-block-container': ['jds_content_block']
              }
            ]
          }
        ]
      },
      'jds_badge'
    ]
  },
  base: {
    'root-container': {
      'flex-direction': '{s_0}',
      size: '{s_2}',
      'justify-content': '{s_4}',
      'align-items': '{s_6}',
      'border-radius': '{s_7}',
      overflow: 'hidden'
    },
    'inner-stack-container': {
      'flex-direction': '{s_1}',
      size: '{s_3}',
      'justify-content': '{s_5}',
      'align-items': '{alignItems.end}',
      overflow: 'hidden'
    },
    'blur-image': {
      height: '{s_8}',
      width: '{s_9}',
      'object-fit': 'cover',
      'blur-radius': '{s_10}',
      'z-index': 0
    },
    'internal-vertical-container': {
      'flex-direction': '{s_12}',
      size: '{s_13}',
      'justify-content': '{s_14}',
      'align-items': '{s_15}',
      'z-index': 1
    },
    'image-container': {
      'flex-direction': '{s_16}',
      'justify-content': '{s_17}',
      'align-items': '{s_18}',
      height: '{s_19}',
      width: '{s_20}',
      overflow: 'hidden'
    },
    jds_image: {
      aspectRatio: 'horizontal_16_9',
      roundedCorner: false,
      imageFocus: 'center'
    },
    jds_badge: {
      kind: 'service',
      'padding-top': '{s_21}',
      'padding-left': '{s_22}',
      'z-index': 1
    },
    'content-block-container': {
      'background-color': '{s_23}',
      width: '{s_24}',
      'padding-top': '{s_25}',
      'padding-bottom': '{s_26}',
      'padding-left': '{s_27}',
      'padding-right': '{s_28}'
    },
    jds_content_block: {
      size: 'xtra-xtra-small',
      invertCTA: false,
      ctaWrap: 'horizontal',
      shouldOverrideColor: true,
      singleButton: true,
      width: '{s_29}'
    }
  },
  variant: {
    imageRatio: {
      square: {
        jds_image: {
          aspectRatio: 'square'
        }
      },
      landscape: {
        jds_image: {
          aspectRatio: 'horizontal_4_3'
        }
      },
      portrait: {
        jds_image: {
          aspectRatio: 'vertical_4_5'
        }
      }
    },
    shadow: {
      true: {
        'root-container': {
          'box-shadow': {
            x: 0,
            y: 4,
            blur: 16,
            spread: 0,
            color: '#00000014'
          }
        }
      }
    },
    size: {
      'xtra-small': {
        jds_content_block: {
          size: 'xtra-small'
        }
      },
      small: {
        jds_content_block: {
          size: 'small'
        }
      }
    },
    _hasFullCardCTA: {
      true: {
        'root-container': {
          behavior: {
            hover: {
              'box-shadow': {
                x: 0,
                y: 4,
                blur: 16,
                spread: 0,
                color: '#00000014'
              }
            }
          }
        }
      }
    }
  },
  api: {
    config: {
      size: {
        values: ['xtra-xtra-small', 'xtra-small', 'small'],
        doc_description:
          'Determines the size of the ContentBlock. Size dictates the text and spacing. For size=`xxs`, the layout also changes if only a single CTA is sent.'
      },
      imageRatio: {
        values: ['wide', 'square', 'landscape', 'portrait']
      },
      shadow: {
        values: [false, true],
        doc_description: 'Toggle drop shadow'
      },
      _hasFullCardCTA: {
        values: [false, true]
      }
    },
    data: {
      'root-container': {
        fullCardCTA: {
          type: 'string',
          name: 'fullCardCTA',
          doc_description: 'When present, the entire card is clickable.'
        }
      },
      jds_content_block: {
        title: {
          type: 'string',
          name: 'title',
          doc_description: 'The main title copy.'
        },
        description: {
          type: 'string',
          name: 'description',
          doc_description:
            'Accepts description as string. JSX.Element is also allowed in case you have anchor tags inside your richtext.'
        },
        primaryCTA: {
          type: 'string',
          name: 'primaryCTA'
        },
        children: {
          type: 'slot',
          name: 'children'
        }
      },
      jds_image: {
        src: {
          type: 'image',
          name: 'image'
        },
        imageFocus: {
          type: 'JDSImage.imageFocus',
          name: 'imageFocus'
        }
      },
      'blur-image': {
        value: {
          type: 'image',
          name: 'image'
        }
      },
      jds_badge: {
        label: {
          type: 'string',
          name: 'badgeLabel'
        },
        icon: {
          type: 'icon',
          name: 'badgeIcon'
        }
      }
    },
    events: {
      'root-container': {
        onClick: 'onClick'
      },
      jds_content_block: {
        onPrimaryClick: 'onPrimaryClick'
      }
    },
    a11y: {
      'root-container': {
        name: 'serviceCard'
      },
      jds_content_block: {
        properties: {
          primaryCtaAriaLabel: 'primaryCtaAriaLabel',
          primaryCtaAriaDescribedby: {
            name: 'primaryCtaAriaDescribedby',
            platform: 'web,ios'
          }
        }
      },
      jds_image: {
        properties: {
          ariaLabel: 'imageAriaLabel',
          ariaDescribedby: {
            name: 'imageAriaDescribedby',
            platform: 'web,ios'
          }
        }
      },
      jds_badge: {
        properties: {
          ariaLabel: 'badgeAriaLabel',
          ariaDescribedby: {
            name: 'badgeAriaDescribedby',
            platform: 'web,ios'
          }
        }
      }
    }
  }
};
